import {
  getFontCss,
  HoverableLink,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import AppName from 'src/components/AppName/AppName'
import BrandAgnosticFooter from 'src/components/BrandAgnosticFooter/BrandAgnosticFooter'
import CopyTextLink from 'src/components/CopyTextLink/CopyTextLink'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import SvgArrowLeft from 'src/components/Svgs/SvgArrowLeft'
import UnauthedBoxContainer from 'src/components/UnauthedBoxContainer/UnauthedBoxContainer'
import { SupportedBrands } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import {
  selectBrandColorCta,
  selectBrandName,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

interface HowToJoinProps {
  className?: string
  setShowHowToJoin: (val: boolean) => void
}

const HowToJoin: React.FC<HowToJoinProps> = (props) => {
  const translate = useTranslate()

  const brandName = useSelector(selectBrandName)
  const brandColorCta = useSelector(selectBrandColorCta)
  const { analytics } = useAnalyticsContext()

  const link =
    brandName === SupportedBrands.PacWest
      ? 'creditcards@pacwest.com'
      : brandName === SupportedBrands.Boc
        ? 'creditcards@bancofcal.com'
        : 'https://www.paywithextend.com/get-started'

  return (
    <UnauthedBoxContainer
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
      `}
      hideBrandLogo
      footer={<BrandAgnosticFooter />}
    >
      <div>
        <HoverableLink
          css={css`
            cursor: pointer;
            display: flex;
            align-items: center;
            margin-bottom: 40px;
          `}
          onClick={(): void => props.setShowHowToJoin(false)}
          ariaLabelSource={'Back to sign up'}
        >
          <SvgArrowLeft
            css={css`
              margin-right: 2px;
            `}
            fill={brandColorCta}
          />
          {translate('Back to sign up')}
        </HoverableLink>
        <div>
          <div
            css={css`
              ${getFontCss(
                32,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              margin-bottom: 40px;
            `}
          >
            {translate('How to join your organization in ${appName}', {
              appName: <AppName />,
            })}
          </div>
          <div
            css={css`
              border-radius: 10px;
              border: 1px solid ${styleConstants.colors.gray_medium};
              background-color: ${styleConstants.colors.gray_light};
              padding: 20px;
            `}
          >
            <div
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
                margin-bottom: 20px;
              `}
            >
              {translate('If your company already uses ${appName}', {
                appName: <AppName />,
              })}
            </div>
            <div
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate(
                "You should use it, too! Ask your company's management to see who can send you an invitation.",
              )}
            </div>
          </div>
          {brandName !== SupportedBrands.Jpm && (
            <>
              <div
                css={css`
                  margin: 20px 0px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                `}
              >
                <DividerLineDiv />
                <div
                  css={css`
                    ${getFontCss(
                      16,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.mediumitalic,
                    )};
                  `}
                >
                  {translate('or')}
                </div>
                <DividerLineDiv />
              </div>
              <div
                css={css`
                  border-radius: 10px;
                  border: 1px solid ${styleConstants.colors.gray_medium};
                  background-color: ${styleConstants.colors.gray_light};
                  padding: 20px;
                `}
              >
                <div
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                    margin-bottom: 20px;
                  `}
                >
                  {translate('If your company does not use ${appName}', {
                    appName: <AppName />,
                  })}
                </div>
                <div
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                    margin-bottom: 20px;
                  `}
                >
                  {brandName === SupportedBrands.PacWest ||
                  brandName === SupportedBrands.Boc
                    ? translate(
                        'They should! Have your company management reach out to us at the below email for more information!',
                      )
                    : translate(
                        'They should! Copy the link below and share with company management. Thank you!',
                      )}
                </div>
                <div
                  css={css`
                    display: flex;
                    height: 62px;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      border-radius: 10px 0px 0px 10px;
                      border: 1px solid ${styleConstants.colors.gray_medium};
                      background-color: ${styleConstants.colors.gray_lightest};
                      padding: 20px;
                      ${getFontCss(
                        16,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                      height: 100%;
                      flex-grow: 1;
                    `}
                  >
                    {link}
                  </div>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      border-radius: 0px 10px 10px 0px;
                      border-top: 1px solid ${styleConstants.colors.gray_medium};
                      border-right: 1px solid
                        ${styleConstants.colors.gray_medium};
                      border-bottom: 1px solid
                        ${styleConstants.colors.gray_medium};
                      background-color: ${styleConstants.colors.gray_lightest};
                      padding: 20px;
                      ${getFontCss(
                        16,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                      height: 100%;
                    `}
                  >
                    <CopyTextLink
                      copyValue={link}
                      copyText={
                        <div
                          css={css`
                            ${getFontCss(
                              16,
                              themeConstants.colors.cta,
                              styleConstants.fontFamilies.medium,
                            )};
                          `}
                        >
                          {translate('Copy')}
                        </div>
                      }
                      onClick={(): void => {
                        analytics?.track('how to join an org - copy email', {
                          intent: 'copy email',
                          interaction: 'text link press',
                        })
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </UnauthedBoxContainer>
  )
}

export default HowToJoin
